export class ApiError extends Error {
  constructor({ type, userMessage, status, data, cause, retryAfterSeconds }) {
    super(userMessage);
    this.name = "ApiError";
    this.type = type; // 'NETWORK' | 'HTTP' | 'TIMEOUT' | 'RATE_LIMIT' | ...
    this.status = status;
    this.data = data;
    this.cause = cause;
    this.userMessage = userMessage;
    this.retryAfterSeconds = retryAfterSeconds; // number | undefined
  }
}

function parseRetryAfterSeconds(retryAfterHeader) {
  if (!retryAfterHeader) return undefined;

  // Pode vir como segundos: "30"
  if (/^\d+$/.test(retryAfterHeader)) return Number(retryAfterHeader);

  // Ou como data HTTP: "Wed, 21 Oct 2015 07:28:00 GMT"
  const dt = Date.parse(retryAfterHeader);
  if (!Number.isNaN(dt)) {
    const diff = Math.ceil((dt - Date.now()) / 1000);
    return diff > 0 ? diff : 0;
  }

  return undefined;
}

function mapHttpMessage({ url, status, data, retryAfterSeconds }) {
  // Se quiser priorizar mensagem do backend:
  const backendMsg = data?.error || data?.message;

  if (status === 429) {
    // mensagem específica pro login (opcional)
    if (url.includes("/auth/login")) {
      return retryAfterSeconds
        ? `Muitas tentativas de login. Tente novamente em ${retryAfterSeconds}s.`
        : "Muitas tentativas de login. Tente novamente em instantes.";
    }

    return retryAfterSeconds
      ? `Muitas requisições. Tente novamente em ${retryAfterSeconds}s.`
      : "Muitas requisições. Tente novamente em instantes.";
  }

  // Exemplo que você já queria (401/403):
  if (url.includes("/auth/login") && (status === 401 || status === 403)) {
    return "Usuário ou senha inválidos.";
  }

  if (backendMsg) return backendMsg;
  return `Erro no servidor (HTTP ${status}).`;
}

export async function http(url, options = {}) {
  const { timeoutMs = 15000, ...fetchOptions } = options;

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { ...fetchOptions, signal: controller.signal });
    const data = await res.json().catch(() => null);

    if (!res.ok) {
      const retryAfterSeconds = parseRetryAfterSeconds(res.headers.get("Retry-After"));
      const type = res.status === 429 ? "RATE_LIMIT" : "HTTP";

      throw new ApiError({
        type,
        status: res.status,
        data,
        retryAfterSeconds,
        userMessage: mapHttpMessage({ url, status: res.status, data, retryAfterSeconds }),
      });
    }

    return data;
  } catch (err) {
    if (err instanceof ApiError) throw err;

    // rede / timeout
    if (err?.name === "AbortError") {
      throw new ApiError({ type: "TIMEOUT", userMessage: "Tempo esgotado. Tente novamente.", cause: err });
    }

    const msg = String(err?.message || "").toLowerCase();
    if (err instanceof TypeError && (msg.includes("failed to fetch") || msg.includes("networkerror"))) {
      throw new ApiError({ type: "NETWORK", userMessage: "Erro de comunicação com o servidor.", cause: err });
    }

    throw new ApiError({ type: "UNKNOWN", userMessage: "Erro inesperado.", cause: err });
  } finally {
    clearTimeout(id);
  }
}
